import { createElement } from "react";
import { LiveComponentSource, LiveComponentSourceProps, VendedLiveSource } from ".";
import { ExperienceProviderConfig } from "../experience-providers";
import { getInjectedInterceptors } from "../interceptors";
import { createScriptElement, retryWithExponentialBackoff } from "./federationUtils";
import { LiveComponentSourceBase } from "./LiveComponentSourceBase";
import { RemoteComponentProps } from "./RemoteComponent";

/* Webpack Types */
type Scope = unknown;

/* Factory function that returns the requested module from the container */
type Factory = () => any;

/* Weback default variable that initializes the shared scope and adds all known provided modules from local build or remote container build */
declare const __webpack_init_sharing__: (shareScope: string) => Promise<void>;

/* Another default Webpack variable, which initializes the exposed module or the container */
declare const __webpack_share_scopes__: { default: Scope };

/* Webpack container */
export interface Container {
  init(shareScope: Scope): void;
  get(module: string): Promise<Factory>;
}

/**
 * Get's the remote's code and appends it to the body of the document. The promise resolves
 * when the remote script is successfully loaded on the body of the application.
 */
export const injectRemoteIntoDOMAndFetch = (url: string, remoteName: string): Promise<Container> =>
  new Promise((resolve, reject) => {
    // We attach a script element to the DOM so the browser can fetch the remoteEntry.js file
    const scriptElement = createScriptElement(url);

    scriptElement.onerror = (err) => {
      console.error(err);
      reject(new Error(`Failed to inject ${remoteName} script into DOM. ${err}`));
    };

    // When the script is loaded we need to resolve the promise back to Module Federation
    scriptElement.onload = () => {
      // The script is now loaded on window using the name defined within the remote
      const proxy = {
        get: (request: Request) => (window as any)[remoteName].get(request),
        init: (arg: any) => {
          try {
            return (window as any)[remoteName].init(arg);
          } catch (err) {
            console.error(err);
            console.error(`Failed to initialize remote: ${remoteName}. ${err}`);
            reject(err);
          }
        },
      };
      console.log(`Succesfully initialized remote: ${remoteName}.`);
      resolve(proxy as unknown as Container);
    };

    // Lastly we inject the script tag into the document's head to trigger the script load
    document.head.appendChild(scriptElement);
  });

/**
 * Initialized correct scopes for webpack, attempts to retrieve remote from window, then returns
 * the module. (Console timing function for now - but this could be tracked on a CW metric.)
 */
export const loadRemoteComponent = (remoteUrl: string, liveReq: RemoteComponentProps) => async () => {
  const { experienceProviderName: remoteName, componentName: moduleName } = liveReq;
  console.time(`LoadingRemoteComponent: [${remoteName}]`);
  if (!(remoteName in window)) {
    await __webpack_init_sharing__("default");

    const fetchedContainer = await retryWithExponentialBackoff(
      injectRemoteIntoDOMAndFetch(`${remoteUrl.replace(/\/$/, "")}/_next/static/chunks/remoteEntry.js`, remoteName),
      2,
      500
    );

    fetchedContainer.init(__webpack_share_scopes__["default"]);
  }

  // Remote is loaded in window
  const container = (window as any)[remoteName];
  const factory = await container.get(`./${moduleName}`);
  const Module = factory();
  console.timeEnd(`LoadingRemoteComponent: [${remoteName}]`);

  // TODO: get rid of try-catch & fallback after all XPS have merged latest xb-scripts: https://tasks.awstcprojects.com/browse/PLATFORM-5980
  try {
    const { __interceptorChain = [], __additionalProps, __lcType } = (Module.default as VendedLiveSource) ?? {};

    // inject Platform provided interceptors
    const { earlyInterceptors, lateInterceptors } = getInjectedInterceptors(__lcType);
    const interceptors = [...lateInterceptors, ...__interceptorChain, ...earlyInterceptors];

    // TODO - add unit tests https://tasks.awstcprojects.com/browse/PLATFORM-6249
    const Component: LiveComponentSource = interceptors.reduce(
      (liveResp, interceptor) => interceptor(liveReq, liveResp),
      Module.default
    );

    const arbManifest = (await container.get("/arbManifest.js"))().default;
    const resolveResourcePack = async (url?: string) => (await container.get(url))();

    return {
      default: (props: LiveComponentSourceProps) =>
        // eslint-disable-next-line react/no-children-prop
        createElement(LiveComponentSourceBase, {
          arbManifest,
          resolveResourcePack,
          children: createElement(Component, { ...props, ...__additionalProps }),
        }),
    };
  } catch (e: unknown) {
    console.log("Failed to load component with translation assets, reverting to old flow for now.");
    return Module;
  }
};

export const findXpsComponentEndpoint = (remoteName: string, remotes: ExperienceProviderConfig[]): string => {
  const remote = remotes?.find((r) => r.name === remoteName);
  return remote?.componentEndpoint || "";
};
